// src/belediye/redux/slices/belediyeReportV1Slice.js
// Report V1 (Applications + Advanced Logs / Close Approval History) — BACKEND-ALIGNED
//
// Backend canonical expectations:
// - ui_mode: simple | advanced
// - datasets: ["applications", "status_logs", "forward_logs", "operation_logs", "close_request_history"]
// - unit_ids + units_mode
// - statuses, neighborhoods, columns
// - close_request_statuses
// - close_request_requested_by / close_request_decided_by / close_request_applied_by
//
// Notes:
// - applications ana dataset olarak düşünülür
// - advanced dataset'ler yalnız president/admin benzeri kullanıcıya açılır
// - export/query için GET/POST desteği reportApi üzerinden opsiyoneldir

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchReportsConfig,
  fetchReportsOptions,
  fetchReportsQuery,
  startReportExport,
  getReportExportStatus,
  downloadReportExportById,
} from "../../services/reportApi";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Async export zamanlaması: 66 dk üst sınır (backend Celery hard-limit 65dk'nın
// hemen üstü — büyük rapor bitene kadar beklesin), 2 sn poll aralığı
const EXPORT_DEADLINE_MS = 66 * 60 * 1000;
const EXPORT_POLL_MS = 2000;

/* =============================================================================
   Debug utils
============================================================================= */

const DEBUG =
  (typeof process !== "undefined" &&
    process?.env?.NODE_ENV !== "production" &&
    process?.env?.REACT_APP_REPORT_DEBUG !== "0") ||
  false;

const log = (...args) => DEBUG && console.log("[ReportV1Slice]", ...args);
const logError = (...args) => DEBUG && console.error("[ReportV1Slice]", ...args);

const safeErrorMessage = (err) => {
  const resp = err?.response;
  if (resp?.data) {
    if (typeof resp.data === "string") return resp.data;
    if (resp.data.detail) return resp.data.detail;
    try {
      return JSON.stringify(resp.data);
    } catch {
      return "İstek hatası oluştu.";
    }
  }
  return err?.message || "Beklenmeyen hata oluştu.";
};

const deepClone = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj || {}));
  } catch {
    return { ...(obj || {}) };
  }
};

const coerceInt = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const coerceBool = (v) => {
  if (v === true || v === "true" || v === 1 || v === "1") return true;
  if (v === false || v === "false" || v === 0 || v === "0") return false;
  return null;
};

const isDateLike = (v) => v instanceof Date && !Number.isNaN(v.getTime());

const coerceDateTime = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (isDateLike(v)) return v.toISOString();
  return v;
};

const ensureArray = (v) => (v === null || v === undefined ? [] : Array.isArray(v) ? v : [v]);

const normalizePrimitive = (item) => {
  if (item === null || item === undefined) return null;
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") return item;

  if (typeof item === "object") {
    if (item.value !== undefined && item.value !== null) return item.value;
    if (item.id !== undefined && item.id !== null) return item.id;
    if (item.code !== undefined && item.code !== null) return item.code;
    if (item.slug !== undefined && item.slug !== null) return item.slug;
    if (item.key !== undefined && item.key !== null) return item.key;
  }

  return String(item);
};

const uniq = (arr) => {
  const seen = new Set();
  const out = [];

  (arr || []).forEach((x) => {
    const key = x === null || x === undefined ? "" : String(x);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(x);
  });

  return out;
};

const ALL = "__all__";

const normalizeStringList = (v) =>
  uniq(
    ensureArray(v)
      .map(normalizePrimitive)
      .filter((x) => x !== null && x !== undefined)
      .map((x) => String(x).trim())
      .filter((x) => x && x !== ALL)
  );

const normalizeIntList = (v) =>
  uniq(
    ensureArray(v)
      .map(normalizePrimitive)
      .map((x) => coerceInt(x))
      .filter((x) => x !== null)
  );

const stripEmpty = (obj) => {
  const out = { ...(obj || {}) };

  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (v === undefined || v === null) delete out[k];
    if (Array.isArray(v) && v.length === 0) delete out[k];
    if (typeof v === "string" && v.trim() === "") delete out[k];
  });

  return out;
};

const stableStringify = (obj) => {
  try {
    const keys = Object.keys(obj || {}).sort();
    return JSON.stringify(obj || {}, keys);
  } catch {
    return "";
  }
};

/* =============================================================================
   Tables parsing
============================================================================= */

const getTableKey = (t) => t?.dataset || t?.name || t?.key || null;

const tablesListToMap = (tables) => {
  const out = {};
  (tables || []).forEach((t) => {
    const key = getTableKey(t);
    if (!key) return;
    out[key] = t;
  });
  return out;
};

/* =============================================================================
   Download helper
============================================================================= */

// Kullanıcının verdiği rapor adını güvenli bir dosya adına çevirir (.xlsx eklenir)
const sanitizeDownloadName = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "";
  // Dosya sistemi için sakıncalı karakterleri temizle
  let clean = raw.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  if (!/\.xlsx$/i.test(clean)) clean += ".xlsx";
  return clean;
};

const triggerBrowserDownload = (blob, filename) => {
  if (typeof window === "undefined" || !blob) return;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "rapor.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/* =============================================================================
   Dataset / columns defaults
============================================================================= */

const DATASET_APPLICATIONS = "applications";
const DATASET_STATUS_LOGS = "status_logs";
const DATASET_FORWARD_LOGS = "forward_logs";
const DATASET_OPERATION_LOGS = "operation_logs";
const DATASET_CLOSE_REQUEST_HISTORY = "close_request_history";

const ADVANCED_DATASET_ORDER = [
  DATASET_STATUS_LOGS,
  DATASET_FORWARD_LOGS,
  DATASET_OPERATION_LOGS,
  DATASET_CLOSE_REQUEST_HISTORY,
];

const BASIC_APPLICATION_COLUMNS_FALLBACK = [
  "tracking_number",
  "citizen_name",
  "citizen_surname",
  "subject",
  "description",
  "created_at",
  "unit",
  "status",
  "neighborhood",
  "reviewed_at",
  "resolved_at",
  "last_forward_to_unit",
  "last_forward_at",
  "close_request_status",
  "close_request_requested_at",
  "close_request_decided_at",
  "close_request_applied_at",
  "final_citizen_note",
  "closure_path",
];

const ADV_APPLICATION_COLUMNS_FALLBACK = [
  "tracking_number",
  "citizen_name",
  "citizen_surname",
  "subject",
  "description",
  "created_at",
  "unit",
  "status",
  "neighborhood",
  "address",
  "status_date",
  "reviewed_by",
  "reviewed_at",
  "resolved_by",
  "resolved_at",
  "last_forward_to_unit",
  "last_forward_at",
  "last_forward_by",
  "last_forward_note",
  "close_request_status",
  "close_request_requested_by",
  "close_request_requested_at",
  "close_request_decided_by",
  "close_request_decided_at",
  "close_request_final_status",
  "close_request_citizen_note",
  "final_citizen_note",
  "final_citizen_note_by",
  "close_request_applied",
  "close_request_applied_at",
  "closure_path",
];

const getAppColumns = (configData, mode /* "basic"|"advanced" */) => {
  const allowed = configData?.whitelists?.applications?.columns_allowed || null;
  const pick = mode === "advanced" ? ADV_APPLICATION_COLUMNS_FALLBACK : BASIC_APPLICATION_COLUMNS_FALLBACK;

  if (!Array.isArray(allowed) || !allowed.length) return pick;

  const allowedSet = new Set(allowed);
  const filtered = pick.filter((c) => allowedSet.has(c));
  return filtered.length ? filtered : pick;
};

const inferIsPresidentLike = (configData, optionsData) => {
  const optionsMeta = optionsData?.meta || {};
  if (typeof optionsMeta.is_admin === "boolean") return optionsMeta.is_admin;

  const allowed = optionsData?.datasets_allowed;
  if (Array.isArray(allowed) && allowed.length) {
    return allowed.some((d) => d && d !== DATASET_APPLICATIONS);
  }

  const vis = configData?.visibility || {};
  return !!(vis.unit_multi_select || vis.logs_enabled || vis.advanced_enabled);
};

/* =============================================================================
   Unit context
============================================================================= */

const getHeaderUnitCtx = (filters) => {
  const unitId = coerceInt(filters?.unit_id);
  const unitSlug = String(filters?.unit_slug || "").trim() || null;
  if (unitId) return { unitId };
  if (unitSlug) return { unitSlug };
  return undefined;
};

/* =============================================================================
   Build request params
============================================================================= */

const normalizeForRequest = (raw) => {
  const f = { ...(raw || {}) };

  // UI-only keys
  delete f.selected_unit;
  delete f.selected_units;
  delete f.openDropdown;
  delete f.advanced_datasets;
  delete f.max_page_size;

  // stable defaults
  f.view_mode = f.view_mode || "applications";
  f.date_mode = f.date_mode || "applications";
  f.unit_scope = f.unit_scope || "current";
  f.ui_mode = f.ui_mode || "simple";
  f.page = coerceInt(f.page) || 1;
  f.page_size = coerceInt(f.page_size) || 25;

  // dates
  if (f.date_from !== undefined) f.date_from = coerceDateTime(f.date_from);
  if (f.date_to !== undefined) f.date_to = coerceDateTime(f.date_to);

  // compat aliases
  if (f.statuses === undefined && f.status !== undefined) f.statuses = f.status;
  if (f.status === undefined && f.statuses !== undefined) f.status = f.statuses;

  if (f.neighborhoods === undefined && f.neighborhood !== undefined) f.neighborhoods = f.neighborhood;
  if (f.neighborhood === undefined && f.neighborhoods !== undefined) f.neighborhood = f.neighborhoods;

  if (f.unit_ids === undefined && f.units !== undefined) f.unit_ids = f.units;
  if (f.units === undefined && f.unit_ids !== undefined) f.units = f.unit_ids;

  if (f.operation_action === undefined && f.operation_actions !== undefined) {
    f.operation_action = f.operation_actions;
  }
  delete f.operation_actions;

  // lists
  if (f.unit_ids !== undefined) f.unit_ids = normalizeIntList(f.unit_ids);
  if (f.units !== undefined) f.units = normalizeIntList(f.units);

  if (f.neighborhoods !== undefined) f.neighborhoods = normalizeIntList(f.neighborhoods);

  if (f.statuses !== undefined) f.statuses = normalizeStringList(f.statuses);
  if (f.status !== undefined) f.status = normalizeStringList(f.status);

  if (f.datasets !== undefined) f.datasets = normalizeStringList(f.datasets);
  if (f.columns !== undefined) f.columns = normalizeStringList(f.columns);
  if (f.operation_action !== undefined) f.operation_action = normalizeStringList(f.operation_action);
  if (f.close_request_statuses !== undefined) {
    f.close_request_statuses = normalizeStringList(f.close_request_statuses);
  }

  // scalars
  if (f.forwarded_only !== undefined) f.forwarded_only = coerceBool(f.forwarded_only);

  if (f.application_id !== undefined) f.application_id = coerceInt(f.application_id);
  if (f.reviewed_by !== undefined) f.reviewed_by = coerceInt(f.reviewed_by);
  if (f.resolved_by !== undefined) f.resolved_by = coerceInt(f.resolved_by);
  if (f.forward_by !== undefined) f.forward_by = coerceInt(f.forward_by);
  if (f.staff_user_ids !== undefined) f.staff_user_ids = normalizeIntList(f.staff_user_ids);

  if (f.close_request_requested_by !== undefined) {
    f.close_request_requested_by = coerceInt(f.close_request_requested_by);
  }
  if (f.close_request_decided_by !== undefined) {
    f.close_request_decided_by = coerceInt(f.close_request_decided_by);
  }
  if (f.close_request_applied_by !== undefined) {
    f.close_request_applied_by = coerceInt(f.close_request_applied_by);
  }

  if (f.forward_by === undefined && f.forwarded_by !== undefined) {
    f.forward_by = coerceInt(f.forwarded_by);
  }
  delete f.forwarded_by;

  // strings
  if (f.ordering !== undefined) f.ordering = String(f.ordering || "");
  if (f.tracking_number !== undefined) {
    f.tracking_number = String(f.tracking_number || "").trim() || null;
  }
  if (f.search !== undefined) {
    f.search = String(f.search || "").trim() || null;
  }
  if (f.primary_dataset !== undefined) {
    f.primary_dataset = String(f.primary_dataset || "").trim() || null;
  }

  // optional active unit selectors
  if (f.unit_id !== undefined) f.unit_id = coerceInt(f.unit_id);
  if (f.unit_slug !== undefined) {
    f.unit_slug = String(f.unit_slug || "").trim() || null;
  }

  return stripEmpty(f);
};

const buildAdvancedDatasets = (raw) => {
  const picked = normalizeStringList(raw);
  const set = new Set();

  picked.forEach((d) => {
    if (ADVANCED_DATASET_ORDER.includes(d)) set.add(d);
  });

  return Array.from(set);
};

const applyRoleAndUiRules = ({ isPresidentLike, advancedOpen, filters, configData, optionsData }) => {
  const out = { ...(filters || {}) };

  out.view_mode = "applications";
  out.date_mode = out.date_mode || "applications";
  out.unit_scope = out.unit_scope || "current";

  const allowedDatasets = Array.isArray(optionsData?.datasets_allowed)
    ? optionsData.datasets_allowed
    : [DATASET_APPLICATIONS];

  const allowedSet = new Set(allowedDatasets);

  if (!isPresidentLike) {
    out.ui_mode = "simple";
    out.datasets = [DATASET_APPLICATIONS];
    out.columns = getAppColumns(configData, "basic");
    out.export_mode = "applications_only";

    delete out.units;
    delete out.unit_ids;
    delete out.units_mode;
    delete out.primary_dataset;

    return out;
  }

  if (!advancedOpen) {
    out.ui_mode = "simple";
    out.datasets = [DATASET_APPLICATIONS];
    out.columns = getAppColumns(configData, "basic");
    out.export_mode = out.export_mode || "applications_only";
    out.primary_dataset = DATASET_APPLICATIONS;

    const unitIds = normalizeIntList(out.unit_ids ?? out.units);
    if (unitIds.length) {
      out.units_mode = "selected";
      out.unit_ids = unitIds;
      out.units = unitIds;
    } else {
      out.units_mode = "all";
      delete out.unit_ids;
      delete out.units;
    }

    return out;
  }

  out.ui_mode = "advanced";
  out.columns = getAppColumns(configData, "advanced");

  const advSelected = buildAdvancedDatasets(out.advanced_datasets || out.datasets || []);
  const datasetSet = new Set([DATASET_APPLICATIONS]);

  advSelected.forEach((d) => {
    if (allowedSet.has(d)) datasetSet.add(d);
  });

  out.datasets = Array.from(datasetSet);
  out.primary_dataset =
    allowedSet.has(out.primary_dataset) && out.primary_dataset
      ? out.primary_dataset
      : DATASET_APPLICATIONS;

  const unitIds = normalizeIntList(out.unit_ids ?? out.units);
  if (unitIds.length) {
    out.units_mode = "selected";
    out.unit_ids = unitIds;
    out.units = unitIds;
  } else {
    out.units_mode = "all";
    delete out.unit_ids;
    delete out.units;
  }

  return out;
};

const buildQueryParams = ({ configDefaults, appliedFilters }) => {
  const base = { ...(configDefaults || {}) };
  const merged = { ...base, ...(appliedFilters || {}) };
  return normalizeForRequest(merged);
};

const buildExportParams = ({ configDefaults, appliedFilters, exportOverrides }) => {
  const base = buildQueryParams({
    configDefaults,
    appliedFilters: { ...(appliedFilters || {}), ...(exportOverrides || {}) },
  });

  if (!base.export_mode) base.export_mode = "applications_only";
  return normalizeForRequest(base);
};

/* =============================================================================
   Thunks
============================================================================= */

export const loadReportV1Config = createAsyncThunk(
  "belediyeReportV1/loadConfig",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchReportsConfig();
      return data;
    } catch (err) {
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const loadReportV1Options = createAsyncThunk(
  "belediyeReportV1/loadOptions",
  async (params, { rejectWithValue }) => {
    try {
      const data = await fetchReportsOptions(params || {});
      return { data, params: params || {} };
    } catch (err) {
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const refreshReportV1Options = createAsyncThunk(
  "belediyeReportV1/refreshOptions",
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState().belediyeReportV1;
      const applied = state?.filters?.applied || {};

      const unitIds = normalizeIntList(applied.unit_ids ?? applied.units);
      const finalParams = { ...(params || {}) };

      if (unitIds.length) {
        finalParams.unit_ids = unitIds;
        finalParams.units = unitIds;
      } else {
        delete finalParams.unit_ids;
        delete finalParams.units;
      }

      const data = await fetchReportsOptions(finalParams);
      return { data, params: finalParams };
    } catch (err) {
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const runReportV1Query = createAsyncThunk(
  "belediyeReportV1/runQuery",
  async (payload, { getState, rejectWithValue, requestId }) => {
    try {
      const state = getState().belediyeReportV1;
      const configData = state?.config?.data || null;
      const optionsData = state?.options?.data || null;
      const configDefaults = state?.filters?.defaults || null;

      const advancedOpen = !!state?.ui?.advancedOpen;
      const isPresidentLike = inferIsPresidentLike(configData, optionsData);

      const overrides = payload?.filters || payload || {};
      const requestOptions = payload?.requestOptions || {};

      let applied = { ...(state?.filters?.applied || {}), ...(overrides || {}) };

      applied = applyRoleAndUiRules({
        isPresidentLike,
        advancedOpen,
        filters: applied,
        configData,
        optionsData,
      });

      const params = buildQueryParams({ configDefaults, appliedFilters: applied });
      const unitCtx = getHeaderUnitCtx(params);

      log("runQuery:start", { requestId, params, unitCtx, requestOptions });

      const data = await fetchReportsQuery(params, unitCtx, requestOptions);

      return { data, uiParams: params };
    } catch (err) {
      logError("runQuery:error", requestId, err);
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const applyDraftAndRunReportV1Query = createAsyncThunk(
  "belediyeReportV1/applyAndRunQuery",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(applyDraftFilters());
      const action = await dispatch(runReportV1Query(payload));
      if (runReportV1Query.rejected.match(action)) {
        return rejectWithValue(action.payload || "Query başarısız.");
      }
      return { ok: true };
    } catch (err) {
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const exportReportV1Xlsx = createAsyncThunk(
  "belediyeReportV1/exportXlsx",
  async (payload, { getState, rejectWithValue, requestId, dispatch, signal }) => {
    try {
      const state = getState().belediyeReportV1;
      const configData = state?.config?.data || null;
      const optionsData = state?.options?.data || null;
      const configDefaults = state?.filters?.defaults || null;

      const advancedOpen = !!state?.ui?.advancedOpen;
      const isPresidentLike = inferIsPresidentLike(configData, optionsData);

      const exportOverrides = payload?.filters || payload || {};
      const requestOptions = payload?.requestOptions || {};

      const lastUiParams = state?.query?.lastParams || null;
      const base = lastUiParams ? deepClone(lastUiParams) : deepClone(state?.filters?.applied || {});

      let applied = applyRoleAndUiRules({
        isPresidentLike,
        advancedOpen,
        filters: base,
        configData,
        optionsData,
      });

      if (!applied.export_mode) {
        applied.export_mode = isPresidentLike && advancedOpen ? "full" : "applications_only";
      }
      if (!isPresidentLike) applied.export_mode = "applications_only";

      applied.columns = getAppColumns(configData, advancedOpen && isPresidentLike ? "advanced" : "basic");
      applied = { ...applied, ...(exportOverrides || {}) };

      const params = buildExportParams({
        configDefaults,
        appliedFilters: applied,
        exportOverrides: {},
      });

      const unitCtx = getHeaderUnitCtx(params);

      log("exportXlsx:start", { requestId, params, unitCtx, requestOptions });

      // ── Async akış: start → poll → download ────────────────────────────
      // Büyük raporlar worker'da üretilir; gunicorn/gateway timeout'una takılmaz.
      const startRes = await startReportExport(params, unitCtx);
      const jobId = startRes?.job_id;
      if (!jobId) {
        return rejectWithValue("Rapor işi başlatılamadı.");
      }

      const startedAt = Date.now();
      dispatch(
        exportProgress({ percent: 0, message: "Sıraya alındı", jobId, startedAt })
      );

      // Poll döngüsü — hazır/hata/zaman aşımı
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (signal?.aborted) {
          return rejectWithValue("İşlem iptal edildi.");
        }
        if (Date.now() - startedAt > EXPORT_DEADLINE_MS) {
          return rejectWithValue(
            "Rapor 10 dakika içinde hazırlanamadı. Lütfen tarih aralığını/filtreleri daraltıp tekrar deneyin."
          );
        }

        let st;
        try {
          st = await getReportExportStatus(jobId);
        } catch (e) {
          // Geçici ağ/gateway hatası — bir sonraki turda tekrar dene
          await sleep(EXPORT_POLL_MS);
          continue;
        }

        const stState = st?.state;

        if (stState === "ready") {
          dispatch(
            exportProgress({ percent: 100, message: "İndiriliyor", jobId, startedAt })
          );
          break;
        }

        if (stState === "error") {
          return rejectWithValue(st?.error || "Rapor oluşturulamadı.");
        }

        dispatch(
          exportProgress({
            percent: Number(st?.percent) || 0,
            message: st?.message || "Hazırlanıyor",
            jobId,
            startedAt,
          })
        );

        await sleep(EXPORT_POLL_MS);
      }

      const { blob, filename } = await downloadReportExportById(jobId);

      // Kullanıcı bir rapor adı belirttiyse dosya onunla iner (aksi halde sunucu adı)
      const forcedName = sanitizeDownloadName(payload?.downloadName);
      const finalName = forcedName || filename || "rapor.xlsx";
      triggerBrowserDownload(blob, finalName);

      return { ok: true, filename: finalName, params };
    } catch (err) {
      logError("exportXlsx:error", requestId, err);
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

export const initReportV1 = createAsyncThunk(
  "belediyeReportV1/init",
  async ({ autoQuery = true } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const cfg = await dispatch(loadReportV1Config());
      if (loadReportV1Config.rejected.match(cfg)) {
        return rejectWithValue(cfg.payload || "Config yüklenemedi.");
      }

      const opt = await dispatch(loadReportV1Options({}));
      if (loadReportV1Options.rejected.match(opt)) {
        return rejectWithValue(opt.payload || "Options yüklenemedi.");
      }

      if (autoQuery) {
        await dispatch(runReportV1Query());
      }

      const st = getState().belediyeReportV1;
      if (!st?.filters?.draft || !Object.keys(st.filters.draft).length) {
        // defaults already initialized by config fulfilled
      }

      return { ok: true };
    } catch (err) {
      return rejectWithValue(safeErrorMessage(err));
    }
  }
);

/* =============================================================================
   Slice
============================================================================= */

const initialState = {
  config: { loading: false, error: null, data: null },
  options: {
    loading: false,
    error: null,
    data: null,
    lastParams: null,
    refreshLoading: false,
    refreshError: null,
    refreshLastParams: null,
  },
  filters: {
    defaults: null,
    draft: { advanced_datasets: [] },
    applied: { advanced_datasets: [] },
  },
  ui: { advancedOpen: false, openDropdown: null },
  query: {
    loading: false,
    error: null,
    lastParams: null,
    lastUpdatedAt: null,
    lastRequestId: null,
  },
  results: { raw: null, summary: null, effectiveFilters: null, tablesByDataset: {} },
  exportXlsx: {
    loading: false,
    error: null,
    lastParams: null,
    lastFilename: null,
    percent: 0,
    message: "",
    jobId: null,
    startedAt: null,
  },
};

const syncUnitAliasesInFilterObj = (obj) => {
  if (!obj) return obj;

  const unitIds = normalizeIntList(obj.unit_ids ?? obj.units);
  if (unitIds.length) {
    obj.unit_ids = unitIds;
    obj.units = unitIds;
  } else {
    if (obj.unit_ids) obj.unit_ids = [];
    if (obj.units) obj.units = [];
  }

  return obj;
};

const syncNeighborhoodAliasesInFilterObj = (obj) => {
  if (!obj) return obj;

  if (obj.neighborhoods === undefined && obj.neighborhood !== undefined) {
    obj.neighborhoods = obj.neighborhood;
  }
  if (obj.neighborhood === undefined && obj.neighborhoods !== undefined) {
    obj.neighborhood = obj.neighborhoods;
  }

  return obj;
};

const syncStatusAliasesInFilterObj = (obj) => {
  if (!obj) return obj;

  if (obj.statuses === undefined && obj.status !== undefined) {
    obj.statuses = obj.status;
  }
  if (obj.status === undefined && obj.statuses !== undefined) {
    obj.status = obj.statuses;
  }

  return obj;
};

const syncOperationAliasesInFilterObj = (obj) => {
  if (!obj) return obj;

  if (obj.operation_action === undefined && obj.operation_actions !== undefined) {
    obj.operation_action = obj.operation_actions;
  }

  return obj;
};

const syncDatasetState = (obj) => {
  if (!obj) return obj;
  obj.advanced_datasets = buildAdvancedDatasets(obj.advanced_datasets || obj.datasets || []);
  return obj;
};

const belediyeReportV1Slice = createSlice({
  name: "belediyeReportV1",
  initialState,
  reducers: {
    setDraftFilter(state, action) {
      const { key, value } = action.payload || {};
      state.filters.draft = state.filters.draft || {};
      state.filters.draft[key] = value;

      if (key === "unit_ids" || key === "units") {
        syncUnitAliasesInFilterObj(state.filters.draft);
      }
      if (key === "neighborhoods" || key === "neighborhood") {
        syncNeighborhoodAliasesInFilterObj(state.filters.draft);
      }
      if (key === "statuses" || key === "status") {
        syncStatusAliasesInFilterObj(state.filters.draft);
      }
      if (key === "operation_action" || key === "operation_actions") {
        syncOperationAliasesInFilterObj(state.filters.draft);
      }
      if (key === "advanced_datasets" || key === "datasets") {
        syncDatasetState(state.filters.draft);
      }
    },

    setDraftFiltersBulk(state, action) {
      const patch = action.payload || {};
      state.filters.draft = { ...(state.filters.draft || {}), ...patch };
      syncUnitAliasesInFilterObj(state.filters.draft);
      syncNeighborhoodAliasesInFilterObj(state.filters.draft);
      syncStatusAliasesInFilterObj(state.filters.draft);
      syncOperationAliasesInFilterObj(state.filters.draft);
      syncDatasetState(state.filters.draft);
    },

    resetDraftToApplied(state) {
      state.filters.draft = deepClone(state.filters.applied || {});
    },

    resetDraftToDefaults(state) {
      const defaults = state.filters.defaults || {};
      state.filters.draft = { ...deepClone(defaults), advanced_datasets: [] };
    },

    applyDraftFilters(state) {
      state.filters.applied = deepClone(state.filters.draft || {});
    },

    setAdvancedOpen(state, action) {
      state.ui.advancedOpen = !!action.payload;
    },

    setOpenDropdown(state, action) {
      state.ui.openDropdown = action.payload ?? null;
    },

    clearReportV1Errors(state) {
      state.config.error = null;
      state.options.error = null;
      state.options.refreshError = null;
      state.query.error = null;
      state.exportXlsx.error = null;
    },

    // Async export ilerlemesi (poll döngüsünden dispatch edilir)
    exportProgress(state, action) {
      const p = action.payload || {};
      if (p.percent !== undefined) state.exportXlsx.percent = Number(p.percent) || 0;
      if (p.message !== undefined) state.exportXlsx.message = p.message || "";
      if (p.jobId !== undefined) state.exportXlsx.jobId = p.jobId;
      if (p.startedAt !== undefined) state.exportXlsx.startedAt = p.startedAt;
    },

    clearReportV1Results(state) {
      state.results = { raw: null, summary: null, effectiveFilters: null, tablesByDataset: {} };
      state.query.lastParams = null;
      state.query.lastUpdatedAt = null;
    },
  },

  extraReducers: (builder) => {
    builder
      /* ---------------- config ---------------- */
      .addCase(loadReportV1Config.pending, (state) => {
        state.config.loading = true;
        state.config.error = null;
      })

      .addCase(loadReportV1Config.fulfilled, (state, action) => {
        state.config.loading = false;
        state.config.data = action.payload || null;
        state.config.error = null;

        const backendDefaults = action.payload?.defaults || {};

        const uiDefaults = {
          ...deepClone(backendDefaults),
          view_mode: "applications",
          date_mode: "applications",
          unit_scope: backendDefaults.unit_scope || "current",
          ui_mode: backendDefaults.ui_mode || "simple",
          page: backendDefaults.page || 1,
          page_size: backendDefaults.page_size || 25,

          unit_ids: [],
          units: [],
          units_mode: "all",

          neighborhoods: [],
          neighborhood: [],

          statuses: [],
          status: [],

          datasets: [DATASET_APPLICATIONS],
          primary_dataset: DATASET_APPLICATIONS,

          columns: getAppColumns(action.payload || null, "basic"),
          operation_action: [],
          advanced_datasets: [],

          close_request_statuses: [],
          close_request_requested_by: null,
          close_request_decided_by: null,
          close_request_applied_by: null,
        };

        state.filters.defaults = deepClone(uiDefaults);

        const hasApplied = state.filters.applied && Object.keys(state.filters.applied).length > 0;
        if (!hasApplied) state.filters.applied = deepClone(uiDefaults);

        state.filters.draft = deepClone(state.filters.applied);
        state.ui.advancedOpen = false;
        state.ui.openDropdown = null;
      })

      .addCase(loadReportV1Config.rejected, (state, action) => {
        state.config.loading = false;
        state.config.error = action.payload || "Config yüklenemedi.";
      })

      /* ---------------- options ---------------- */
      .addCase(loadReportV1Options.pending, (state) => {
        state.options.loading = true;
        state.options.error = null;
      })

      .addCase(loadReportV1Options.fulfilled, (state, action) => {
        state.options.loading = false;
        state.options.data = action.payload?.data || null;
        state.options.lastParams = action.payload?.params || null;
        state.options.error = null;
      })

      .addCase(loadReportV1Options.rejected, (state, action) => {
        state.options.loading = false;
        state.options.error = action.payload || "Options yüklenemedi.";
      })

      .addCase(refreshReportV1Options.pending, (state) => {
        state.options.refreshLoading = true;
        state.options.refreshError = null;
      })

      .addCase(refreshReportV1Options.fulfilled, (state, action) => {
        state.options.refreshLoading = false;
        state.options.refreshError = null;
        state.options.refreshLastParams = action.payload?.params || null;
        state.options.data = action.payload?.data || state.options.data;
      })

      .addCase(refreshReportV1Options.rejected, (state, action) => {
        state.options.refreshLoading = false;
        state.options.refreshError = action.payload || "Options yenileme başarısız.";
      })

      /* ---------------- query ---------------- */
      .addCase(runReportV1Query.pending, (state, action) => {
        state.query.loading = true;
        state.query.error = null;
        state.query.lastRequestId = action.meta.requestId;
      })

      .addCase(runReportV1Query.fulfilled, (state, action) => {
        if (state.query.lastRequestId && state.query.lastRequestId !== action.meta.requestId) return;

        state.query.loading = false;
        state.query.error = null;
        state.query.lastParams = action.payload?.uiParams || null;
        state.query.lastUpdatedAt = Date.now();

        const data = action.payload?.data || null;
        state.results.raw = data;
        state.results.summary = data?.summary || null;
        state.results.effectiveFilters = data?.effective_filters || null;
        state.results.tablesByDataset = tablesListToMap(data?.tables || []);
      })

      .addCase(runReportV1Query.rejected, (state, action) => {
        state.query.loading = false;
        state.query.error = action.payload || "Query başarısız.";
      })

      /* ---------------- export ---------------- */
      .addCase(exportReportV1Xlsx.pending, (state) => {
        state.exportXlsx.loading = true;
        state.exportXlsx.error = null;
        state.exportXlsx.percent = 0;
        state.exportXlsx.message = "Başlatılıyor";
        state.exportXlsx.jobId = null;
        state.exportXlsx.startedAt = Date.now();
      })

      .addCase(exportReportV1Xlsx.fulfilled, (state, action) => {
        state.exportXlsx.loading = false;
        state.exportXlsx.error = null;
        state.exportXlsx.lastParams = action.payload?.params || null;
        state.exportXlsx.lastFilename = action.payload?.filename || null;
        state.exportXlsx.percent = 100;
        state.exportXlsx.message = "";
        state.exportXlsx.jobId = null;
        state.exportXlsx.startedAt = null;
      })

      .addCase(exportReportV1Xlsx.rejected, (state, action) => {
        state.exportXlsx.loading = false;
        state.exportXlsx.error = action.payload || "Export başarısız.";
        state.exportXlsx.percent = 0;
        state.exportXlsx.message = "";
        state.exportXlsx.jobId = null;
        state.exportXlsx.startedAt = null;
      })

      /* ---------------- init ---------------- */
      .addCase(initReportV1.rejected, (state, action) => {
        if (!state.config.error) {
          state.config.error = action.payload || "Init başarısız.";
        }
      });
  },
});

/* =============================================================================
   Exports
============================================================================= */

export const {
  setDraftFilter,
  setDraftFiltersBulk,
  resetDraftToApplied,
  resetDraftToDefaults,
  applyDraftFilters,
  setAdvancedOpen,
  setOpenDropdown,
  clearReportV1Errors,
  clearReportV1Results,
  exportProgress,
} = belediyeReportV1Slice.actions;

export const selectReportV1Config = (s) => s?.belediyeReportV1?.config;
export const selectReportV1Options = (s) => s?.belediyeReportV1?.options;
export const selectReportV1Query = (s) => s?.belediyeReportV1?.query;
export const selectReportV1Export = (s) => s?.belediyeReportV1?.exportXlsx;

export const selectReportV1DraftFilters = (s) => s?.belediyeReportV1?.filters?.draft || {};
export const selectReportV1AppliedFilters = (s) => s?.belediyeReportV1?.filters?.applied || {};
export const selectReportV1Defaults = (s) => s?.belediyeReportV1?.filters?.defaults || {};

export const selectReportV1AdvancedOpen = (s) => !!s?.belediyeReportV1?.ui?.advancedOpen;
export const selectReportV1OpenDropdown = (s) => s?.belediyeReportV1?.ui?.openDropdown || null;

export const selectReportV1Summary = (s) => s?.belediyeReportV1?.results?.summary;
export const selectReportV1TablesByDataset = (s) => s?.belediyeReportV1?.results?.tablesByDataset || {};
export const selectReportV1EffectiveFilters = (s) => s?.belediyeReportV1?.results?.effectiveFilters || null;

export const selectReportV1ConfigData = (s) => s?.belediyeReportV1?.config?.data || null;
export const selectReportV1OptionsData = (s) => s?.belediyeReportV1?.options?.data || null;

export const selectReportV1IsPresidentLike = (s) => {
  const cfg = selectReportV1ConfigData(s);
  const opt = selectReportV1OptionsData(s);
  return inferIsPresidentLike(cfg, opt);
};

export const selectReportV1HasDraftChanges = (s) => {
  const draft = selectReportV1DraftFilters(s);
  const applied = selectReportV1AppliedFilters(s);
  return stableStringify(draft) !== stableStringify(applied);
};

export const selectReportV1CanExport = (s) => {
  const q = selectReportV1Query(s);
  const ex = selectReportV1Export(s);
  const hasVisibleResult = !!q?.lastParams && !q?.error;
  if (!hasVisibleResult) return false;
  if (q?.loading) return false;
  if (ex?.loading) return false;
  return true;
};

export default belediyeReportV1Slice.reducer;